import {
    AutoAwesome as AIIcon,
    ViewComfy as CompositeIcon,
    Image as ImageIcon,
    MusicNote as MusicIcon,
    Refresh as RegenerateIcon,
    CheckCircle as SuccessIcon,
    Movie as VideoIcon,
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    LinearProgress,
    Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
// Removed unused imports: AlbumArtDisplay, ProcessingMethodIndicator, ComprehensiveTagsDisplay
import aiContractService, {
    type AIGenerationRequest,
} from '../../services/aiContractService';
import CompactAISuccessDisplay from './CompactAISuccessDisplay';
import TagQualityIndicator from './TagQualityIndicator';
// Removed unused import: AIContractValidator
import { albumArtService } from '../../services/albumArtService';
import { environmentSafeLog } from '../../utils/environment';
import { EnhancedErrorHandler } from '../../utils/errorHandler';
import { enhanceTagsWithVariantName } from '../../utils/variantTagGenerator';

interface AIMetadataGeneratorProps {
  fileUrl?: string;
  file?: File; // File object (deprecated - use getCurrentFile instead)
  getCurrentFile?: () => File | undefined; // Function to retrieve current file on-demand (preferred - no caching)
  fileName?: string;
  fileType?: string;
  layer: string;
  categoryCode: string;
  subcategoryCode: string;
  onDescriptionGenerated: (description: string) => void;
  onTagsGenerated: (tags: string[]) => void;
  onAdditionalMetadataGenerated?: (metadata: any) => void; // Phase 2A: Album art and enhanced metadata
  currentDescription?: string;
  currentTags?: string[];
  disabled?: boolean;

  // Enhanced AI Integration props
  shortDescription?: string; // Creator's Description from the form (deprecated - use getCurrentDescription instead)
  getCurrentDescription?: () => string; // Function to retrieve current description on-demand (preferred - no caching)
  categoryName?: string;
  subcategoryName?: string;
  layerName?: string;
  thumbnailUrl?: string; // For video layers
  imageUrl?: string; // For image layers
  componentMetadata?: any[]; // For composite layers
  selectedComponents?: any[]; // Selected components for C layer
  onComponentsSelected?: (components: any[]) => void; // Callback when components are selected
  onAIGenerationReady?: () => void; // Callback to notify parent when AI is ready to be triggered
  triggerGeneration?: number; // When this number changes, trigger AI generation

  // 🛡️ INHERITANCE SUPPORT - For variant assets
  inheritedValues?: Record<string, any>; // Values inherited from base asset
  baseAsset?: any; // Base asset data for reference

  // 🏷️ VARIANT TAG ENHANCEMENT - For AlgoRhythm optimization
  variantName?: string; // Variant name to generate specific tags
  starName?: string; // Star name to add as tag (for S layer)

  // 🎵 SONGS LAYER UI CLEANUP - Hide regeneration buttons for songs layer
  hideRegenerationButtons?: boolean; // Hide "Improve Tags Only" and regeneration buttons
}

// Using AI Response Contract for backend integration

interface GenerationState {
  isGenerating: boolean;
  lastGenerated: {
    description?: string;
    tags?: string[];
    timestamp?: Date;
    additionalMetadata?: any;
    songMetadata?: any; // CRITICAL FIX: Add songMetadata field to store song information
    starsMetadata?: any; // CRITICAL FIX: Add starsMetadata field to store star information
    looksMetadata?: any; // CRITICAL FIX: Add looksMetadata field to store looks information
    movesMetadata?: any; // AI CONTRACT: Add movesMetadata field for contract compatibility
    worldsMetadata?: any; // AI CONTRACT: Add worldsMetadata field for contract compatibility
    compositeMetadata?: any; // AI CONTRACT: Add compositeMetadata field for contract compatibility
    layerMetadata?: any; // V2.0: Add layerMetadata field for unified layer metadata
    processingType?: string;
    warning?: string; // NEW: Add warning field for content policy issues
  } | null;
  error: string | object | null;
  progress: string;
  progressStep: number;
  totalSteps: number;
  useEnhancedAI: boolean;
}

const AIMetadataGenerator: React.FC<AIMetadataGeneratorProps> = ({
  fileUrl,
  file,
  getCurrentFile,
  fileName,
  fileType,
  layer,
  categoryCode,
  subcategoryCode,
  onDescriptionGenerated,
  onTagsGenerated,
  onAdditionalMetadataGenerated,
  currentDescription,
  currentTags = [],
  disabled = false,
  // Enhanced AI Integration props
  shortDescription,
  getCurrentDescription,
  categoryName,
  subcategoryName,
  layerName,
  thumbnailUrl,
  imageUrl,
  componentMetadata,
  selectedComponents,
  onComponentsSelected,
  onAIGenerationReady,
  triggerGeneration,

  // 🛡️ INHERITANCE SUPPORT - For variant assets
  inheritedValues,
  baseAsset,

  // 🏷️ VARIANT TAG ENHANCEMENT - For AlgoRhythm optimization
  variantName,
  starName,

  // 🎵 SONGS LAYER UI CLEANUP - Hide regeneration buttons for songs layer
  hideRegenerationButtons = false,
}) => {
  const [state, setState] = useState<GenerationState>({
    isGenerating: false,
    lastGenerated: null,
    error: null,
    progress: '',
    progressStep: 0,
    totalSteps: 0,
    useEnhancedAI: !!shortDescription, // Use enhanced AI if Creator's Description is provided
  });

  // Check if backend AI is available
  const [isConfigured] = useState(true); // Assume backend is available initially

  const getEffectiveCurrentDescription = useCallback((): string => {
    if (getCurrentDescription) {
      const raw = getCurrentDescription();
      return typeof raw === 'string' ? raw.trim() : String(raw || '').trim();
    }

    return typeof shortDescription === 'string' ? shortDescription.trim() : '';
  }, [getCurrentDescription, shortDescription]);

  const getEffectiveCurrentFile = useCallback((): File | undefined => {
    if (file instanceof File) {
      return file;
    }

    const current = getCurrentFile?.();
    return current instanceof File ? current : undefined;
  }, [file, getCurrentFile]);

  // Helper function to get layer-specific icon
  const getLayerIcon = (layer: string) => {
    const icons: Record<string, React.ComponentType<any>> = {
      G: MusicIcon,
      S: ImageIcon,
      L: ImageIcon,
      M: VideoIcon,
      W: VideoIcon,
      C: CompositeIcon,
    };
    const IconComponent = icons[layer] || AIIcon;
    return <IconComponent sx={{ mr: 1, color: 'primary.main' }} />;
  };

  // Helper function to determine if enhanced AI should be used
  const shouldUseEnhancedAI = () => {
    const result =
      state.useEnhancedAI &&
      shortDescription &&
      shortDescription.trim().length > 0;
    environmentSafeLog('[AI DEBUG] shouldUseEnhancedAI check:', {
      useEnhancedAI: state.useEnhancedAI,
      hasShortDescription: !!shortDescription,
      shortDescription,
      shortDescriptionLength: shortDescription?.trim().length || 0,
      result,
    });
    return result;
  };

  // Helper function to check if we're in composite workflow and components are available
  const isCompositeWorkflowReady = useCallback(() => {
    if (layer !== 'C') return true; // Non-composite layers are always ready
    return selectedComponents && selectedComponents.length > 0;
  }, [layer, selectedComponents]);

  // Helper function to check if we should show composite pending state
  const shouldShowCompositePending = useCallback(() => {
    return (
      layer === 'C' && (!selectedComponents || selectedComponents.length === 0)
    );
  }, [layer, selectedComponents]);

  const canGenerateMetadata = useCallback(() => {
    if (!isCompositeWorkflowReady()) {
      return false;
    }

    if (layer === 'C') {
      return true;
    }

    return !!getEffectiveCurrentFile();
  }, [getEffectiveCurrentFile, isCompositeWorkflowReady, layer]);

  // 🏷️ Helper function to enhance tags with variant-specific tags for AlgoRhythm
  const enhanceTagsWithVariant = (originalTags: string[]): string[] => {
    if (!variantName) {
      return originalTags;
    }

    const enhancedTags = enhanceTagsWithVariantName(
      originalTags,
      variantName,
      layer,
      starName
    );

    if (enhancedTags.length > originalTags.length) {
      environmentSafeLog('🏷️ [VARIANT TAGS] Enhanced tags for AlgoRhythm:', {
        original: originalTags,
        enhanced: enhancedTags,
        variantName,
        layer,
        added: enhancedTags.slice(originalTags.length),
      });
    }

    return enhancedTags;
  };

  // Helper function to build AI contract request
  const buildContractRequest = (): AIGenerationRequest => {
    // Provide appropriate placeholder URL based on layer if no fileUrl is available
    const getPlaceholderUrl = (layer: string): string => {
      const placeholders = {
        G: 'https://placeholder.example.com/song.mp3',
        S: 'https://placeholder.example.com/star.jpg',
        L: 'https://placeholder.example.com/look.jpg',
        M: 'https://placeholder.example.com/moves.mp4',
        W: 'https://placeholder.example.com/world.mp4',
        C: 'https://placeholder.example.com/composite.jpg',
      };
      return (
        placeholders[layer as keyof typeof placeholders] ||
        'https://placeholder.example.com/asset.bin'
      );
    };

    const currentDescription = getEffectiveCurrentDescription();

    const currentFile = getEffectiveCurrentFile();

    if (!currentFile) {
      const errorMsg = 'File is required for AI metadata generation. Please ensure a file is uploaded.';
      environmentSafeLog('❌ [API REQUEST] File validation failed:', {
        hasGetCurrentFile: !!getCurrentFile,
        hasFileProp: !!file,
        getCurrentFileResult: getCurrentFile ? getCurrentFile() : 'function not provided',
        timestamp: new Date().toISOString(),
      });
      throw new Error(errorMsg);
    }

    const request: AIGenerationRequest = {
      layer,
      creatorDescription: currentDescription || fileName || '',
      file: currentFile,
      ...(!fileUrl && { fileUrl: getPlaceholderUrl(layer) }), // Include placeholder URL only if no fileUrl provided
      additionalContext: {
        category: categoryCode,
        subcategory: subcategoryCode,
        fileName: fileName || currentFile?.name || 'uploaded-file',
        fileType: fileType || currentFile?.type || 'unknown',
        fileSize: currentFile?.size,
        layerName: layerName || layer,

        // 🛡️ INHERITANCE CONTEXT - Pass inheritance data for variant assets
        ...(inheritedValues && { inheritedValues }),
        ...(baseAsset && { baseAsset }),
        categoryName: categoryName || categoryCode,
        subcategoryName: subcategoryName || subcategoryCode,
        componentMetadata:
          layer === 'C' ? selectedComponents : componentMetadata,
        thumbnailUrl: thumbnailUrl,
        imageUrl: imageUrl,
        // 🛡️ INHERITANCE SUPPORT - Pass inherited values for variant assets
        inheritedValues: inheritedValues,
        baseAsset: baseAsset,
      },
    };

    environmentSafeLog('📦 [AIMetadataGenerator] Request includes file:', {
      hasFile: !!request.file,
      fileName: request.file?.name,
      fileSize: request.file?.size,
      fileType: request.file?.type,
      isFileInstance: request.file instanceof File,
    });

    environmentSafeLog('📦 [AIMetadataGenerator] Building contract request:', {
      layer: request.layer,
      creatorDescription: request.creatorDescription,
      creatorDescriptionLength: request.creatorDescription?.length || 0,
      hasFile: !!request.file,
      fileName: request.file?.name || fileName,
      fileSize: request.file?.size,
      fileType: request.file?.type,
      isFileInstance: request.file instanceof File,
      hasFileUrl: !!request.fileUrl,
      usingGetCurrentFile: !!getCurrentFile,
      usingGetCurrentDescription: !!getCurrentDescription,
      shortDescriptionProvided: !!shortDescription,
      shortDescriptionValue: shortDescription,
      timestamp: new Date().toISOString(),
    });

    return request;
  };

  // Helper function to set progress with layer-specific messages and step tracking
  const setProgress = (message: string, step?: number, totalSteps?: number) => {
    setState(prev => ({
      ...prev,
      progress: message,
      ...(step !== undefined && { progressStep: step }),
      ...(totalSteps !== undefined && { totalSteps })
    }));
  };

  // Enhanced progress messages for different layers
  const getProgressMessages = (layer: string): string[] => {
    const messages: Record<string, string[]> = {
      G: [
        'Extracting song information...',
        'Searching music databases...',
        'Generating enhanced description and tags...',
      ],
      S: ['Analyzing performer image...', 'Generating description and tags...'],
      L: [
        'Analyzing look/style image...',
        'Generating description and tags...',
      ],
      M: ['Analyzing movement video...', 'Generating description and tags...'],
      W: [
        'Analyzing environment/world...',
        'Generating description and tags...',
      ],
      C: [
        'Aggregating component metadata...',
        'Generating composite description...',
      ],
    };
    return (
      messages[layer] || [
        'Analyzing file...',
        'Generating description and tags...',
      ]
    );
  };

  const handleGenerateMetadata = useCallback(async () => {
    // Starting metadata generation

    const currentFile = getEffectiveCurrentFile();
    const currentDescription = getEffectiveCurrentDescription();

    if (!currentFile && layer !== 'C') {
      setState(prev => ({
        ...prev,
        error: 'No file available for analysis. Please upload a file first.',
      }));
      return;
    }

    // Composite layer validation
    if (
      layer === 'C' &&
      (!selectedComponents || selectedComponents.length === 0)
    ) {
      setState(prev => ({
        ...prev,
        error:
          'Please select components in Step 5 before generating AI metadata for composite assets.',
      }));
      return;
    }

    const progressMessages = getProgressMessages(layer);
    setState(prev => ({
      ...prev,
      isGenerating: true,
      error: null,
      progress: 'Initializing AI generation...',
      progressStep: 0,
      totalSteps: progressMessages.length + 2, // +2 for initialization and completion
    }));

    let result: {
      description: string;
      tags: string[];
      additionalMetadata?: any;
      // Layer-specific metadata fields
      looksMetadata?: any;
      starsMetadata?: any;
      songMetadata?: any;
      movesMetadata?: any;
      worldsMetadata?: any;
      compositesMetadata?: any;
    } | null = null;

    try {
      environmentSafeLog('🚀 [AI CONTRACT] Generation started with:', {
        layer,
        hasCurrentFile: !!currentFile,
        hasCurrentDescription: !!currentDescription,
      });

      // Show layer-specific progress messages with step tracking
      setProgress('Initializing AI generation...', 1, progressMessages.length + 2);
      await new Promise(resolve => setTimeout(resolve, 100));

      for (let i = 0; i < progressMessages.length; i++) {
        setProgress(progressMessages[i], i + 2, progressMessages.length + 2);
        if (i < progressMessages.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      // Use AI Contract Service for backend integration with retry mechanism
      environmentSafeLog(
        '🚀 [AI CONTRACT] Using AI Contract Service for generation'
      );
      const contractRequest = buildContractRequest();
      environmentSafeLog('🔎 [AI REQUEST] Calling extract metadata endpoint', {
        layer,
        hasFile: !!contractRequest.file,
      });

      // Implement retry mechanism for AI generation
      const contractResponse = await EnhancedErrorHandler.withRetry(
        () => aiContractService.generateMetadata(contractRequest),
        {
          maxAttempts: 2, // Limited retries for AI to avoid long delays
          delay: 1500,
          backoffMultiplier: 1.5
        },
        (attempt, error) => {
          setProgress(`Retrying AI generation (attempt ${attempt + 1})...`, state.progressStep, state.totalSteps);
          environmentSafeLog(`🔄 [RETRY] AI generation attempt ${attempt + 1} failed:`, error.message);
        }
      );
      environmentSafeLog('✅ [AI REQUEST] Extract metadata response received', {
        layer,
        processingMethod: contractResponse.processingMethod,
      });

      if (contractResponse.data) {
        // ✅ AI CONTRACT: Transform contract response to expected result format
        environmentSafeLog(
          '✅ [AI CONTRACT] Backend response received:',
          contractResponse
        );

        // 🔍 CRITICAL DEBUGGING: Comprehensive AI response structure analysis
        console.log('🔍 [AI RESPONSE DEBUG] ===== COMPLETE RESPONSE STRUCTURE =====');
        console.log('🔍 [AI RESPONSE DEBUG] contractResponse (full):', JSON.stringify(contractResponse, null, 2));
        console.log('🔍 [AI RESPONSE DEBUG] contractResponse.data:', JSON.stringify(contractResponse.data, null, 2));

        // Check description at multiple paths
        console.log('🔍 [AI DESCRIPTION DEBUG] Description path analysis:', {
          'contractResponse.data.description': contractResponse.data?.description,
          'contractResponse.data.layerMetadata.description': contractResponse.data?.layerMetadata?.description,
          'contractResponse.description': (contractResponse as any).description,
          'description exists at data level': !!contractResponse.data?.description,
          'description exists at layerMetadata level': !!contractResponse.data?.layerMetadata?.description,
          'description exists at root level': !!(contractResponse as any).description,
          'description length (data)': contractResponse.data?.description?.length || 0,
          'description length (layerMetadata)': contractResponse.data?.layerMetadata?.description?.length || 0,
          'description content preview (data)': contractResponse.data?.description?.substring(0, 100) || 'N/A',
          'description content preview (layerMetadata)': contractResponse.data?.layerMetadata?.description?.substring(0, 100) || 'N/A'
        });

        // Check tags at multiple paths
        console.log('🔍 [AI TAGS DEBUG] Tags path analysis:', {
          'contractResponse.data.tags': contractResponse.data?.tags,
          'contractResponse.data.layerMetadata.tags': contractResponse.data?.layerMetadata?.tags,
          'contractResponse.tags': (contractResponse as any).tags,
          'tags exists at data level': !!contractResponse.data?.tags,
          'tags exists at layerMetadata level': !!contractResponse.data?.layerMetadata?.tags,
          'tags exists at root level': !!(contractResponse as any).tags,
          'tags count (data)': contractResponse.data?.tags?.length || 0,
          'tags count (layerMetadata)': contractResponse.data?.layerMetadata?.tags?.length || 0
        });

        // Check layer-specific metadata
        console.log('🔍 [AI LAYER METADATA DEBUG] Layer metadata analysis:', {
          'has layerMetadata': !!contractResponse.data?.layerMetadata,
          'layerMetadata keys': contractResponse.data?.layerMetadata ? Object.keys(contractResponse.data.layerMetadata) : [],
          'layerMetadata structure': contractResponse.data?.layerMetadata
        });

        console.log('🔍 [AI RESPONSE DEBUG] ===== END STRUCTURE ANALYSIS =====');

        // 🏷️ Enhanced tag extraction logging
        const dataTagsCount = contractResponse.data.tags?.length || 0;
        const nestedTagsCount =
          contractResponse.data.layerMetadata?.tags?.length || 0;
        console.log(
          `🏷️ [TAGS] Found ${dataTagsCount} tags at data.tags, ${nestedTagsCount} tags at layerMetadata.tags`
        );

        // 🏷️ CRITICAL FIX: Check both locations for tags due to double-nesting
        const extractedTags =
          contractResponse.data?.tags ||
          contractResponse.data?.layerMetadata?.tags ||
          [];

        console.log('🏷️ [TAG EXTRACTION] Extracted tags:', {
          extractedTags,
          count: extractedTags.length,
          source: contractResponse.data?.tags
            ? 'data.tags'
            : contractResponse.data?.layerMetadata?.tags
            ? 'layerMetadata.tags'
            : 'none',
        });

        // 🆘 EMERGENCY FALLBACK: Generate tags from description if none found
        let finalTags = extractedTags;
        if (!finalTags || finalTags.length === 0) {
          console.warn(
            '🚨 [TAG EXTRACTION] No tags found in response, generating fallback tags'
          );
          const description =
            contractResponse.data?.description ||
            contractResponse.data?.layerMetadata?.description ||
            '';
          const descLower = description.toLowerCase();

          const fallbackTags = [];
          // Extract characteristics from description
          if (descLower.includes('female')) fallbackTags.push('female-talent');
          if (descLower.includes('male') && !descLower.includes('female'))
            fallbackTags.push('male-talent');
          if (descLower.includes('teen')) fallbackTags.push('teen-performer');
          if (descLower.includes('pop')) fallbackTags.push('pop-star');
          if (descLower.includes('blonde')) fallbackTags.push('blonde-hair');
          if (descLower.includes('pink')) fallbackTags.push('pink-hair');
          if (descLower.includes('brown eyes')) fallbackTags.push('brown-eyes');

          // Always add base tags
          fallbackTags.push('stars', 'talent', 'performer');

          finalTags = fallbackTags;
          console.log(
            '🔧 [TAG EXTRACTION] Generated fallback tags:',
            finalTags
          );
        }

        // 🔍 DESCRIPTION EXTRACTION DEBUG: Track where description comes from
        const extractedDescription = (() => {
          const dataDescription = contractResponse.data.description;
          const layerMetadataDescription = contractResponse.data.layerMetadata?.description;
          const fallbackDescription = 'Generated description';

          console.log('🔍 [DESCRIPTION EXTRACTION] Description extraction analysis:', {
            'data.description available': !!dataDescription,
            'data.description length': dataDescription?.length || 0,
            'layerMetadata.description available': !!layerMetadataDescription,
            'layerMetadata.description length': layerMetadataDescription?.length || 0,
            'final description source': dataDescription ? 'data.description' : layerMetadataDescription ? 'layerMetadata.description' : 'fallback'
          });

          const finalDescription = dataDescription || layerMetadataDescription || fallbackDescription;
          console.log('🔍 [DESCRIPTION EXTRACTION] Final description preview:', finalDescription.substring(0, 100) + '...');

          return finalDescription;
        })();

        // CRITICAL FIX: Ensure layerType is set in layerMetadata if missing
        const layerMetadataWithType = {
          ...contractResponse.data.layerMetadata,
        };

        // Map layer codes to layerType if layerType is missing
        if (!layerMetadataWithType.layerType && layer) {
          const layerCodeToTypeMap: Record<string, string> = {
            G: 'songs',
            S: 'stars',
            L: 'looks',
            M: 'moves',
            W: 'worlds',
            C: 'composite',
          };
          const mappedLayerType = layerCodeToTypeMap[layer];
          if (mappedLayerType) {
            layerMetadataWithType.layerType = mappedLayerType;
            environmentSafeLog(
              `🔧 [AIMetadataGenerator] Set layerType from layer code: ${layer} → ${mappedLayerType}`
            );
          }
        }

        result = {
          description: extractedDescription,
          // 🏷️ CRITICAL FIX: Use enhanced tag extraction with fallback
          tags: finalTags,
          additionalMetadata: {
            processingMethod: contractResponse.processingMethod,
            confidence: contractResponse.confidence,
            fallbackUsed: contractResponse.fallbackUsed,
            approach: contractResponse.approach,
            processingTimeMs: contractResponse.processingTimeMs || 0,
            aiEnhanced: contractResponse.aiEnhanced || false,
            // ARCHITECTURAL SIMPLIFICATION: Include album art separately for backward compatibility
            albumArt: contractResponse.data.layerMetadata?.albumArt,
          },
          // CLEAN ARCHITECTURE: Use only layerMetadata (no layer-specific duplicates)
          layerMetadata: layerMetadataWithType,
        } as any;
      } else {
        environmentSafeLog(
          '❌ [AI CONTRACT] Backend generation failed:',
          contractResponse
        );
        throw new Error(
          contractResponse.error?.message || 'AI Contract generation failed'
        );
      }

      setProgress('Finalizing metadata...', state.totalSteps, state.totalSteps);
      await new Promise(resolve => setTimeout(resolve, 200));

      environmentSafeLog('🚀 [AI CONTRACT] Generation completed:', result);
    } catch (error) {
      console.error('🚨 [ENHANCED AI] Generation failed:', error);

      // Use enhanced error handling for better user experience
      const errorResult = EnhancedErrorHandler.handleError(error, 'AI metadata generation');
      const userMessage = errorResult.userMessage;
      const warning = errorResult.severity === 'warning' ? errorResult.userMessage : '';

      // Log detailed error information for debugging
      environmentSafeLog('🚨 [ERROR HANDLING] Enhanced error details:', {
        userMessage: errorResult.userMessage,
        technicalMessage: errorResult.technicalMessage,
        isRetryable: errorResult.isRetryable,
        suggestedAction: errorResult.suggestedAction,
        errorCode: errorResult.errorCode,
        severity: errorResult.severity
      });

      // CRITICAL FIX: Create a fallback result if AI generation fails
      if (
        layer === 'C' &&
        selectedComponents &&
        selectedComponents.length > 0
      ) {
        environmentSafeLog(
          '🔧 [COMPOSITE FALLBACK] Using component metadata despite AI error'
        );

        // Create a basic result from component metadata
        const firstComponent = selectedComponents[0];
        result = {
          description: `Composite asset combining ${
            selectedComponents.length
          } components: ${shortDescription || 'Multiple layers'}`,
          tags: ['composite', 'multi-layer', layer.toLowerCase()],
          additionalMetadata: {},
          // Extract songMetadata from G layer component if available
          songMetadata:
            firstComponent?.layer === 'G' && firstComponent?.songMetadata
              ? firstComponent.songMetadata
              : null,
        } as any;
      }

      // ENHANCED ERROR HANDLING: Ensure userMessage is always a string
      const finalErrorMessage =
        typeof userMessage === 'string'
          ? userMessage
          : 'AI generation failed. Please try again.';
      console.error(
        '🚨 [GENERATION] Setting error state with message:',
        finalErrorMessage
      );
      console.error(
        '🚨 [GENERATION] Error message type:',
        typeof finalErrorMessage
      );

      setState(prev => ({
        ...prev,
        isGenerating: false,
        error: finalErrorMessage,
        progress: '',
        progressStep: 0,
        totalSteps: 0,
        lastGenerated: {
          ...prev.lastGenerated,
          warning,
        },
      }));

      // Still try to use any partial results
      if (result?.description || result?.tags) {
        onDescriptionGenerated(result.description || '');
        onTagsGenerated(result.tags || []);
      }
    }

    // CRITICAL FIX: Process result OUTSIDE the try-catch block to ensure it always runs
    if (result) {
      // Processing additional metadata

      // Looks layer metadata processed

      // ALBUM ART FALLBACK: If Songs layer and no album art from backend, fetch from iTunes
      // Check both V2.0 layerMetadata and legacy songMetadata
      if (
        layer === 'G' &&
        ((result as any).layerMetadata || (result as any).songMetadata)
      ) {
        const songData =
          (result as any).layerMetadata || (result as any).songMetadata;
        const albumArtUrl =
          result.additionalMetadata?.albumArt || (result as any).albumArt;

        // Checking album art status

        if (!albumArtUrl && songData.songName && songData.artistName) {
          console.log(
            '[ALBUM ART FALLBACK] Backend returned no album art, fetching from iTunes...'
          );
          try {
            const albumArtResult = await albumArtService.fetchAlbumArt({
              songName: songData.songName,
              artistName: songData.artistName,
              albumName: songData.albumName,
            });

            if (albumArtResult) {
              console.log(
                '[ALBUM ART FALLBACK] Successfully fetched album art:',
                albumArtResult.url
              );
              // Add album art to result
              if (!result.additionalMetadata) {
                result.additionalMetadata = {};
              }
              result.additionalMetadata.albumArt = albumArtResult.url;
              result.additionalMetadata.albumArtSource = albumArtResult.source;
            } else {
              console.log(
                '[ALBUM ART FALLBACK] No album art found from iTunes'
              );
            }
          } catch (error) {
            console.error(
              '[ALBUM ART FALLBACK] Error fetching album art:',
              error
            );
          }
        }
      }

      // 🔍 FORM SUBMISSION DEBUG: Track description passed to parent
      console.log('🔍 [FORM SUBMISSION] Calling onDescriptionGenerated with:', {
        'description length': result.description?.length || 0,
        'description preview': result.description?.substring(0, 100) + '...',
        'description is valid': !!result.description && result.description !== 'Generated description'
      });

      // Update form fields
      onDescriptionGenerated(result.description);

      // 🏷️ BACKEND TAG FILTERING: Use filteredTags if available, fallback to original tags
      const tagsToUse = (() => {
        // Check if backend provided filtered tags structure
        if ((result as any).filteredTags && Array.isArray((result as any).filteredTags)) {
          console.log('🏷️ [BACKEND FILTERING] Using backend-filtered tags:', (result as any).filteredTags);
          return (result as any).filteredTags;
        }

        // Fallback to original tags if no filtering provided
        const fallbackTags = result.tags || [];
        console.log('🏷️ [FALLBACK] Backend did not provide filteredTags, using original tags:', fallbackTags);
        return fallbackTags;
      })();

      console.log('🏷️ [TAG DEBUG] result.tags (raw):', result.tags);
      console.log('🏷️ [TAG DEBUG] result.filteredTags:', (result as any).filteredTags);
      console.log('🏷️ [TAG DEBUG] result.removedTags:', (result as any).removedTags);
      console.log('🏷️ [TAG DEBUG] tagsToUse (final):', tagsToUse);

      // 🏷️ VARIANT TAG ENHANCEMENT: Enhance tags for AlgoRhythm optimization
      const enhancedTags = enhanceTagsWithVariant(tagsToUse);
      onTagsGenerated(enhancedTags);

      // Phase 2A/2C: Pass all metadata (including album art, songMetadata, and aiMetadata) to parent form
      if (onAdditionalMetadataGenerated) {
        const albumArtUrl =
          result.additionalMetadata?.albumArt || (result as any).albumArt;
        console.log(`[ALBUM ART] URL: ${albumArtUrl ? 'Set' : 'None'}`);

        const allMetadata = {
          ...result.additionalMetadata,
          // V2.0 SCHEMA: Pass layerMetadata instead of layer-specific keys
          ...((result as any).layerMetadata && {
            layerMetadata: (result as any).layerMetadata,
          }),
          ...((result as any).aiMetadata && {
            aiMetadata: (result as any).aiMetadata,
          }),
          // 🏷️ BACKEND TAG FILTERING: Pass tag filtering data to form
          ...((result as any).filteredTags && {
            aiRawTags: result.tags,
            aiFilteredTags: (result as any).filteredTags,
            aiRemovedTags: (result as any).removedTags || [],
          }),
        };

        // All metadata prepared for form

        // Metadata ready for parent component

        onAdditionalMetadataGenerated(allMetadata);
      }

      // Update state with enhanced metadata only if not already set by error handler
      setState(prev => {
        if (!prev.error) {
          // 🏷️ VARIANT TAG ENHANCEMENT: Enhance tags for state storage too
          const enhancedTagsForState = enhanceTagsWithVariant(result!.tags);

          const newLastGenerated = {
            description: result!.description,
            tags: enhancedTagsForState,
            timestamp: new Date(),
            additionalMetadata: result!.additionalMetadata,
            layerMetadata: (() => {
              // 🏷️ VARIANT TAG ENHANCEMENT: Also enhance layerMetadata tags if present
              const originalLayerMetadata = (result as any)?.layerMetadata;
              if (originalLayerMetadata?.tags) {
                return {
                  ...originalLayerMetadata,
                  tags: enhancedTagsForState, // Use the same enhanced tags
                };
              }
              return originalLayerMetadata;
            })(), // V2.0 UNIFIED: Store unified layerMetadata from result
            songMetadata: (result as any)?.songMetadata, // CRITICAL FIX: Store songMetadata from result
            starsMetadata: (result as any)?.starsMetadata, // CRITICAL FIX: Store starsMetadata from result
            looksMetadata: (result as any)?.looksMetadata, // CRITICAL FIX: Store looksMetadata from result
            movesMetadata: (result as any)?.movesMetadata, // CRITICAL FIX: Store movesMetadata from result
            worldsMetadata: (result as any)?.worldsMetadata, // CRITICAL FIX: Store worldsMetadata from result
            compositesMetadata: (result as any)?.compositesMetadata, // CRITICAL FIX: Store compositesMetadata from result
            processingType: 'ai-contract',
          };

          console.log(
            `🎯 [AI SUCCESS] Setting state with ${
              newLastGenerated.tags?.length || 0
            } tags`
          );

          return {
            ...prev,
            isGenerating: false,
            progress: '',
            progressStep: 0,
            totalSteps: 0,
            lastGenerated: newLastGenerated,
          };
        }
        console.log(
          '🚨 [AI SUCCESS STATE DEBUG] NOT setting state because prev.error exists:',
          prev.error
        );
        return prev;
      });
    }
  }, [
    shortDescription,
    layer,
    categoryCode,
    subcategoryCode,
    selectedComponents,
    fileName,
    fileType,
    categoryName,
    subcategoryName,
    layerName,
    thumbnailUrl,
    imageUrl,
    onDescriptionGenerated,
    onTagsGenerated,
    onAdditionalMetadataGenerated,
    buildContractRequest,
    getEffectiveCurrentDescription,
    getEffectiveCurrentFile,
  ]);

  // AUTOMATIC AI TRIGGER: When components are selected for composite layer
  React.useEffect(() => {
    // Only run auto-trigger logic for composite layers to avoid infinite loops
    if (layer !== 'C') {
      return;
    }

    console.log(
      `[AUTO-TRIGGER] Layer: ${layer}, Components: ${
        selectedComponents?.length || 0
      }, Can trigger: ${
        selectedComponents &&
        selectedComponents.length > 0 &&
        !state.isGenerating &&
        !state.lastGenerated
      }`
    );

    if (
      selectedComponents &&
      selectedComponents.length > 0 &&
      !state.isGenerating &&
      !state.lastGenerated
    ) {
      environmentSafeLog(
        '[COMPOSITE AI] Auto-triggering with',
        selectedComponents.length,
        'components'
      );
      environmentSafeLog(
        '[COMPOSITE AI] Selected components:',
        selectedComponents
      );

      // Auto-trigger AI generation when components are available
      setTimeout(() => {
        environmentSafeLog(
          '[COMPOSITE AI] Calling handleGenerateMetadata from auto-trigger'
        );
        handleGenerateMetadata();
      }, 1000); // Small delay to ensure UI state is stable
    }
  }, [
    selectedComponents,
    layer,
    state.isGenerating,
    state.lastGenerated,
    shortDescription,
  ]);

  // Track the last trigger value to prevent infinite loops
  const lastTriggerRef = useRef(0);

  // EXTERNAL TRIGGER: When triggerGeneration prop changes, trigger AI generation
  useEffect(() => {
    if (
      triggerGeneration &&
      triggerGeneration > 0 &&
      triggerGeneration !== lastTriggerRef.current &&
      !state.isGenerating
    ) {
      console.log(
        '[EXTERNAL TRIGGER] Triggering AI generation from external trigger:',
        triggerGeneration
      );
      lastTriggerRef.current = triggerGeneration;
      handleGenerateMetadata();
    }
  }, [triggerGeneration, state.isGenerating, handleGenerateMetadata]);

  // Reset AI state when category changes
  useEffect(() => {
    if (categoryCode) {
      console.log('[AI STATE] Category changed, resetting AI generation state');
      setState(prev => ({
        ...prev,
        isGenerating: false,
        lastGenerated: null,
        error: null,
      }));
    }
  }, [categoryCode]);

  // Reset AI state when subcategory changes
  useEffect(() => {
    if (subcategoryCode) {
      console.log('[AI STATE] Subcategory changed, resetting AI generation state');
      setState(prev => ({
        ...prev,
        isGenerating: false,
        lastGenerated: null,
        error: null,
      }));
    }
  }, [subcategoryCode]);

  // Reset AI state when file is cleared
  useEffect(() => {
    const currentFile = getCurrentFile ? getCurrentFile() : file;
    if (!currentFile) {
      console.log('[AI STATE] File cleared, resetting AI generation state');
      setState(prev => ({
        ...prev,
        isGenerating: false,
        lastGenerated: null,
        error: null,
      }));
    }
  }, [getCurrentFile, file]);

  // IMPORTANT: Step 3 regeneration uses blob URLs which backend can't access
  // The regenerate-description and regenerate-tags endpoints require real file URLs
  // So we use generateEnhancedMetadata which works with blob URLs (proven by "Generate Again")
  // Edit Details page continues to use the specific endpoints (it has real file URLs)
  const handleRegenerateMetadata = async (
    type: 'description' | 'tags' | 'both' = 'both'
  ) => {
    // Starting regeneration
    console.log(`🔄 [REGENERATION] ${type} clicked`);

    const currentFile = getEffectiveCurrentFile();
    const currentDescription = getEffectiveCurrentDescription();

    if (!currentFile && layer !== 'C') {
      console.log(
        '🚫 [REGENERATION DEBUG] Regeneration blocked - no file available'
      );
      return;
    }

    if (!currentDescription) {
      setState(prev => ({
        ...prev,
        error:
          "Please provide a Creator's Description before regenerating AI metadata.",
      }));
      return;
    }

    environmentSafeLog('🔄 [REGENERATION] Starting regeneration', {
      type,
      hasCurrentFile: !!currentFile,
      hasCurrentDescription: !!currentDescription,
      layer,
      category: categoryCode,
      subcategory: subcategoryCode,
    });

    setState(prev => ({
      ...prev,
      isGenerating: true,
      error: null,
      progress: `Regenerating ${type}...`,
      progressStep: 1,
      totalSteps: 3,
    }));

    try {
      environmentSafeLog('🔄 [AI CONTRACT] Regeneration started:', {
        type,
        layer,
        hasCurrentFile: !!currentFile,
      });

      // Use AI Contract Service for regeneration
      setProgress(`Analyzing content for ${type} regeneration...`, 2, 3);
      const contractRequest = buildContractRequest();
      environmentSafeLog('🔎 [AI REQUEST] Calling extract metadata endpoint (regenerate)', {
        layer,
        hasFile: !!contractRequest.file,
      });
      const contractResponse = await aiContractService.generateMetadata(
        contractRequest
      );
      environmentSafeLog('✅ [AI REQUEST] Extract metadata response received (regenerate)', {
        layer,
        processingMethod: contractResponse.processingMethod,
      });

      if (!contractResponse.data) {
        throw new Error(
          contractResponse.error?.message || 'AI Contract regeneration failed'
        );
      }

      // Transform contract response for regeneration with preservation logic
      const result = {
        description: (() => {
          // PRESERVATION FIX: When regenerating tags only, preserve current description
          if (type === 'tags' && state.lastGenerated?.description) {
            environmentSafeLog(
              '🛡️ [PRESERVATION] Preserving existing description during tags-only regeneration'
            );
            return state.lastGenerated.description;
          }
          // For description or both regeneration, use new description
          // 🎯 CRITICAL FIX: Check multiple description paths from backend response
          const description =
            contractResponse.data.description ||
            contractResponse.data.layerMetadata?.description ||
            'Regenerated description';
          environmentSafeLog(
            '🛡️ [DESCRIPTION FIX] Description from backend:',
            description
          );
          return description;
        })(),
        tags: (() => {
          // FIX: When regenerating tags only, ensure we get new tags from backend
          // Check multiple possible locations for tags in the response
          const directTags = contractResponse.data.tags || [];
          const layerMetadataTags =
            contractResponse.data.layerMetadata?.tags || [];
          const aiResponseTags =
            (contractResponse.data as any).aiResponse?.tags ||
            contractResponse.data.layerMetadata?._debug?.aiResponse?.tags ||
            [];

          // Prioritize: direct tags > AI response tags > layer metadata tags
          const newTags =
            directTags.length > 0
              ? directTags
              : aiResponseTags.length > 0
              ? aiResponseTags
              : layerMetadataTags;

          environmentSafeLog('🔍 [TAGS DEBUG] Backend response structure:', {
            hasTags: !!contractResponse.data.tags,
            hasLayerMetadataTags: !!contractResponse.data.layerMetadata?.tags,
            hasAiResponseTags: !!(contractResponse.data as any).aiResponse
              ?.tags,
            hasDebugAiResponseTags:
              !!contractResponse.data.layerMetadata?._debug?.aiResponse?.tags,
            directTagsLength: directTags.length,
            aiResponseTagsLength: aiResponseTags.length,
            layerMetadataTagsLength: layerMetadataTags.length,
            debugAiResponseTagsLength:
              contractResponse.data.layerMetadata?._debug?.aiResponse?.tags
                ?.length || 0,
            newTagsLength: newTags.length,
            existingTagsLength: state.lastGenerated?.tags?.length || 0,
            regenerationType: type,
          });

          if (
            type === 'tags' &&
            newTags.length === 0 &&
            state.lastGenerated?.tags
          ) {
            // If no new tags were generated but we had existing tags, preserve them
            environmentSafeLog(
              '🛡️ [TAGS FIX] No new tags generated, preserving existing tags'
            );
            return state.lastGenerated.tags;
          }
          environmentSafeLog(
            '🏷️ [TAGS FIX] Using new tags from backend:',
            newTags.slice(0, 10),
            `(${newTags.length} total)`
          );
          return newTags;
        })(),
        additionalMetadata: {
          processingMethod: `${contractResponse.processingMethod}-regenerate`,
          confidence: contractResponse.confidence,
          fallbackUsed: contractResponse.fallbackUsed,
          approach: contractResponse.approach,
          processingTimeMs: contractResponse.processingTimeMs || 0,
          aiEnhanced: contractResponse.aiEnhanced || false,
          albumArt: contractResponse.data.layerMetadata?.albumArt,
        },
        // CLEAN ARCHITECTURE: Use only layerMetadata (no layer-specific duplicates)
        layerMetadata: (() => {
          let layerMetadataResult;
          // PRESERVATION FIX: When regenerating tags only, preserve layerMetadata description
          if (type === 'tags' && state.lastGenerated?.layerMetadata) {
            const currentLayerMetadata = state.lastGenerated.layerMetadata;
            const newLayerMetadata = contractResponse.data.layerMetadata;

            // Merge: preserve description, use new tags and other fields
            layerMetadataResult = {
              ...newLayerMetadata,
              description: currentLayerMetadata.description, // Preserve description
              tags: newLayerMetadata?.tags || [], // Use new tags
            };
          } else {
            // For description or both regeneration, use new layerMetadata completely
            layerMetadataResult = contractResponse.data.layerMetadata;
          }

          // CRITICAL FIX: Ensure layerType is set if missing
          if (!layerMetadataResult?.layerType && layer) {
            const layerCodeToTypeMap: Record<string, string> = {
              G: 'songs',
              S: 'stars',
              L: 'looks',
              M: 'moves',
              W: 'worlds',
              C: 'composite',
            };
            const mappedLayerType = layerCodeToTypeMap[layer];
            if (mappedLayerType) {
              layerMetadataResult = {
                ...layerMetadataResult,
                layerType: mappedLayerType,
              };
              environmentSafeLog(
                `🔧 [AIMetadataGenerator Regeneration] Set layerType from layer code: ${layer} → ${mappedLayerType}`
              );
            }
          }

          return layerMetadataResult;
        })(),
      } as any;

      // ALBUM ART FALLBACK: If Songs layer and no album art from backend, fetch from iTunes (regeneration)
      if (layer === 'G' && result.layerMetadata) {
        const songData = result.layerMetadata;
        const albumArtUrl = result.additionalMetadata?.albumArt;

        if (!albumArtUrl && songData.songName && songData.artistName) {
          console.log(
            '[ALBUM ART FALLBACK REGEN] Backend returned no album art, fetching from iTunes...'
          );
          try {
            const albumArtResult = await albumArtService.fetchAlbumArt({
              songName: songData.songName,
              artistName: songData.artistName,
              albumName: songData.albumName,
            });

            if (albumArtResult) {
              console.log(
                '[ALBUM ART FALLBACK REGEN] Successfully fetched album art:',
                albumArtResult.url
              );
              // Add album art to result
              result.additionalMetadata.albumArt = albumArtResult.url;
              result.additionalMetadata.albumArtSource = albumArtResult.source;
            } else {
              console.log(
                '[ALBUM ART FALLBACK REGEN] No album art found from iTunes'
              );
            }
          } catch (error) {
            console.error(
              '[ALBUM ART FALLBACK REGEN] Error fetching album art:',
              error
            );
          }
        }
      }

      // Update only the requested fields
      if (type === 'description' || type === 'both') {
        onDescriptionGenerated(result.description);
      }
      if (type === 'tags' || type === 'both') {
        // 🏷️ VARIANT TAG ENHANCEMENT: Enhance regenerated tags for AlgoRhythm
        const enhancedTags = enhanceTagsWithVariant(result.tags);
        onTagsGenerated(enhancedTags);
      }

      // Phase 2A/2C: Pass additional metadata on regeneration too
      if (onAdditionalMetadataGenerated) {
        const allMetadata = {
          ...result.additionalMetadata,
          // V2.0 SCHEMA: Pass layerMetadata instead of layer-specific keys (regeneration)
          ...((result as any).layerMetadata && {
            layerMetadata: (result as any).layerMetadata,
          }),
          ...((result as any).aiMetadata && {
            aiMetadata: (result as any).aiMetadata,
          }),
        };

        console.log(
          '[PHASE 2A/2C REGEN DEBUG] Original result keys:',
          Object.keys(result)
        );
        console.log(
          '[PHASE 2A/2C REGEN DEBUG] result.layerMetadata:',
          (result as any).layerMetadata
        );
        console.log(
          '[PHASE 2A/2C] Additional metadata passed to form (regeneration):',
          allMetadata
        );

        onAdditionalMetadataGenerated(allMetadata);
      }

      setState(prev => {
        const shouldUpdateTags = type === 'tags' || type === 'both';
        const finalTags = shouldUpdateTags
          ? result.tags
          : prev.lastGenerated?.tags;

        environmentSafeLog(
          '🔍 [STATE DEBUG] Setting tags in state during regeneration:',
          {
            shouldUpdateTags,
            finalTagsLength: finalTags?.length || 0,
            regenerationType: type,
            resultTagsLength: result.tags?.length || 0,
          }
        );

        return {
          ...prev,
          isGenerating: false,
          progress: '',
          lastGenerated: {
            description:
              type === 'description' || type === 'both'
                ? result.description
                : prev.lastGenerated?.description,
            tags: finalTags,
            timestamp: new Date(),
            additionalMetadata: result.additionalMetadata,
            layerMetadata:
              (result as any)?.layerMetadata ||
              prev.lastGenerated?.layerMetadata, // ARCHITECTURAL SIMPLIFICATION: Store layerMetadata
            processingType: 'ai-contract',
          },
        };
      });

      environmentSafeLog('🔄 [AI CONTRACT] Regeneration completed:', result);

      // Update only the requested fields
      if (type === 'description' || type === 'both') {
        onDescriptionGenerated(result.description);
      }
      if (type === 'tags' || type === 'both') {
        // 🏷️ VARIANT TAG ENHANCEMENT: Enhance regenerated tags for AlgoRhythm
        const enhancedTags = enhanceTagsWithVariant(result.tags);
        onTagsGenerated(enhancedTags);
      }

      // Pass additional metadata on regeneration
      if (onAdditionalMetadataGenerated) {
        const allMetadata = {
          ...result.additionalMetadata,
          // V2.0 SCHEMA: Pass layerMetadata instead of layer-specific keys
          ...((result as any).layerMetadata && {
            layerMetadata: (result as any).layerMetadata,
          }),
        };

        onAdditionalMetadataGenerated(allMetadata);
      }

      setState(prev => ({
        ...prev,
        isGenerating: false,
        progress: '',
        progressStep: 0,
        totalSteps: 0,
        lastGenerated: {
          description:
            type === 'description' || type === 'both'
              ? result.description
              : prev.lastGenerated?.description,
          tags:
            type === 'tags' || type === 'both'
              ? result.tags
              : prev.lastGenerated?.tags,
          timestamp: new Date(),
          additionalMetadata: result.additionalMetadata,
          layerMetadata:
            (result as any)?.layerMetadata || prev.lastGenerated?.layerMetadata, // ARCHITECTURAL SIMPLIFICATION: Store layerMetadata
          processingType: 'ai-contract',
        },
      }));
    } catch (error) {
      console.error(
        `[REGENERATION] ${type} regeneration failed:`,
        error instanceof Error ? error.message : error
      );

      // ENHANCED ERROR HANDLING: Ensure string conversion with comprehensive object handling
      let errorMessage: string;
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error && typeof error === 'object') {
        // Handle axios error structure
        if (
          'response' in error &&
          error.response &&
          typeof error.response === 'object' &&
          'data' in error.response
        ) {
          const responseData = error.response.data;
          if (typeof responseData === 'string') {
            errorMessage = responseData;
          } else if (
            responseData &&
            typeof responseData === 'object' &&
            'message' in responseData
          ) {
            errorMessage = String(responseData.message);
          } else if (
            responseData &&
            typeof responseData === 'object' &&
            'error' in responseData
          ) {
            errorMessage = String(responseData.error);
          } else {
            // Safe access to status property
            const status =
              'status' in error.response ? error.response.status : 'unknown';
            errorMessage = `Server error (${status})`;
          }
        } else if ('message' in error) {
          errorMessage = String(error.message);
        } else {
          // For any other object, serialize it safely
          try {
            errorMessage = JSON.stringify(error);
          } catch {
            errorMessage = 'An unknown error occurred';
          }
        }
      } else {
        errorMessage = `Failed to regenerate ${type}`;
      }

      // Error message prepared

      setState(prev => ({
        ...prev,
        isGenerating: false,
        progress: '',
        progressStep: 0,
        totalSteps: 0,
        error: errorMessage,
      }));
    }
  };

  const hasGenerated = state.lastGenerated !== null;
  const showRegenerateOptions = hasGenerated && !state.isGenerating;

  // Show configuration warning if API key is missing
  if (!isConfigured) {
    return (
      <Box sx={{ mb: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Backend AI Service Unavailable</strong>
            <br />
            The AI Contract service is currently unavailable. Please check the
            backend connection to enable AI-powered metadata generation.
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 3 }}>
      {/* Enhanced AI Generation Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        {getLayerIcon(layer)}
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          🤖 Enhanced AI Metadata Generation{' '}
          {layer === 'C' ? '(Composite)' : ''}
        </Typography>
        {hasGenerated && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {state.lastGenerated?.processingType === 'ai-contract' && (
              <Chip
                label="AI Contract"
                color="primary"
                size="small"
                variant="outlined"
              />
            )}
            <Chip
              icon={<SuccessIcon />}
              label="Generated"
              color="success"
              size="small"
            />
          </Box>
        )}
      </Box>

      {/* Enhanced Description with layer-specific guidance */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {shouldUseEnhancedAI() ? (
          <>
            🚀 <strong>Enhanced AI Mode:</strong> Using your Creator's
            Description "{shortDescription}" to generate layer-specific,
            AlgoRhythm-optimized metadata for {layerName || layer} assets.
          </>
        ) : (
          <>
            Let AI analyze your {layerName || layer} asset and automatically
            generate description and tags based on your metadata guide for
            optimal AlgoRhythm compatibility.
          </>
        )}
      </Typography>

      {/* Composite Layer Pending State - Enhanced */}
      {shouldShowCompositePending() && (
        <Alert severity="warning" sx={{ mb: 2, border: '2px solid #ff9800' }}>
          <Typography variant="h6" sx={{ color: '#f57c00', mb: 1 }}>
            🎭 AI Processing for Composite Assets
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>📋 Current Status:</strong> Composite assets generate AI
            metadata after component selection.
            <br />
            <strong>🎯 Next Step:</strong> Continue to Step 5 to select
            component assets, then AI will automatically generate enhanced
            metadata.
          </Typography>
          <Box
            sx={{
              p: 2,
              bgcolor: 'rgba(255, 152, 0, 0.1)',
              borderRadius: 1,
              mb: 2,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
              🤖 What happens next:
            </Typography>
            <Typography variant="body2" component="ul" sx={{ ml: 2, mb: 0 }}>
              <li>Select G, S, L, M, W assets in Step 5</li>
              <li>AI will analyze all component descriptions and tags</li>
              <li>
                Generate unified composite description with intelligent
                aggregation
              </li>
              <li>Create cross-layer compatibility optimized tags</li>
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            💡 <strong>Tip:</strong> The more diverse your component selection,
            the richer your composite metadata will be!
          </Typography>
        </Alert>
      )}

      {/* Composite Layer Ready State */}
      {layer === 'C' &&
        isCompositeWorkflowReady() &&
        !state.isGenerating &&
        !state.lastGenerated && (
          <Alert severity="success" sx={{ mb: 2, border: '2px solid #4caf50' }}>
            <Typography variant="h6" sx={{ color: '#2e7d32', mb: 1 }}>
              ✅ Composite AI Ready - Auto-Processing...
            </Typography>
            <Typography variant="body2">
              <strong>🎉 Components Selected:</strong>{' '}
              {selectedComponents?.length || 0} assets ready for AI processing.
              <br />
              <strong>🤖 Status:</strong> AI metadata generation starting
              automatically with component analysis.
            </Typography>
          </Alert>
        )}

      {/* Composite Layer Auto-Processing State */}
      {layer === 'C' && isCompositeWorkflowReady() && state.isGenerating && (
        <Alert severity="info" sx={{ mb: 2, border: '2px solid #2196f3' }}>
          <Typography variant="h6" sx={{ color: '#1976d2', mb: 1 }}>
            🤖 Auto-Processing Composite AI
          </Typography>
          <Typography variant="body2">
            <strong>📊 Analyzing:</strong> {selectedComponents?.length || 0}{' '}
            component assets for intelligent aggregation.
            <br />
            <strong>⚡ Processing:</strong> Creating unified descriptions and
            cross-layer optimized tags.
          </Typography>
        </Alert>
      )}

      {/* Enhanced Generation Button - Hidden for composite auto-processing */}
      {!hasGenerated && !(layer === 'C' && isCompositeWorkflowReady()) && (
        <Button
          variant="contained"
          startIcon={
            state.isGenerating ? (
              <CircularProgress size={20} />
            ) : (
              getLayerIcon(layer)
            )
          }
          onClick={handleGenerateMetadata}
          disabled={
            disabled ||
            state.isGenerating ||
            !canGenerateMetadata() ||
            !isCompositeWorkflowReady()
          }
          size="large"
          sx={{
            mb: 2,
            ...(layer === 'C' &&
              !isCompositeWorkflowReady() && {
                bgcolor: 'grey.300',
                color: 'grey.600',
                '&:disabled': {
                  bgcolor: 'grey.300',
                  color: 'grey.600',
                },
              }),
          }}
        >
          {state.isGenerating
            ? shouldUseEnhancedAI()
              ? 'Enhanced AI Processing...'
              : 'Analyzing with AI...'
            : layer === 'C' && !isCompositeWorkflowReady()
            ? '⏳ Waiting for Component Selection (Step 5)'
            : shouldUseEnhancedAI()
            ? 'Generate Enhanced Metadata'
            : 'Generate Description & Tags with AI'}
        </Button>
      )}

      {/* Manual trigger button for composite layer if auto-processing failed */}
      {layer === 'C' &&
        isCompositeWorkflowReady() &&
        !hasGenerated &&
        !state.isGenerating && (
          <Button
            variant="outlined"
            startIcon={getLayerIcon(layer)}
            onClick={handleGenerateMetadata}
            disabled={disabled}
            size="large"
            sx={{ mb: 2 }}
          >
            Manual Generate (Backup)
          </Button>
        )}

      {/* Step 3 Regeneration - Show for all layers */}
      {showRegenerateOptions && !hideRegenerationButtons && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Not satisfied with the results?
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RegenerateIcon />}
              onClick={() => handleRegenerateMetadata('both')}
              disabled={state.isGenerating || !canGenerateMetadata()}
              size="small"
            >
              Generate Again
            </Button>
          </Box>
        </Box>
      )}

      {/* Enhanced Processing Status Display */}
      {state.isGenerating && (
        <Card sx={{ mb: 2, border: '2px solid', borderColor: 'primary.main' }}>
          <CardContent sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CircularProgress size={24} sx={{ mr: 2 }} />
              <Typography variant="h6" sx={{ color: 'primary.main' }}>
                🤖 AI Processing in Progress
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ mb: 2, fontWeight: 'medium' }}>
              {state.progress}
            </Typography>

            {state.totalSteps > 0 && (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Step {state.progressStep} of {state.totalSteps}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {Math.round((state.progressStep / state.totalSteps) * 100)}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={(state.progressStep / state.totalSteps) * 100}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            )}

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              💡 This typically takes 2-8 seconds depending on content complexity
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Compact Success Message */}
      {(() => {
        // DEBUG: Log the conditions for showing AI success display
        console.log(
          `🎯 [AI DISPLAY] Should show: ${
            hasGenerated && !state.isGenerating && !state.error
          }`
        );

        // Extract rich AI metadata from nested structure
        const richTags =
          state.lastGenerated?.layerMetadata?.tags ||
          state.lastGenerated?.tags ||
          [];

        console.log(`🎯 [AI RICH DATA] Displaying ${richTags.length} tags`);

        return hasGenerated && !state.isGenerating && !state.error;
      })() && (
        <CompactAISuccessDisplay
          description={(() => {
            const richDescription =
              state.lastGenerated?.layerMetadata?.description ||
              state.lastGenerated?.description ||
              'Generated asset description';
            return richDescription;
          })()}
          tags={(() => {
            // CRITICAL FIX: Prioritize direct tags over layerMetadata.tags during regeneration
            const richTags =
              state.lastGenerated?.tags ||
              state.lastGenerated?.layerMetadata?.tags ||
              [];

            // DEBUGGING: Log tag sources to identify issue
            console.log(
              '🏷️ [TAGS DEBUG] state.lastGenerated:',
              state.lastGenerated
            );
            console.log(
              '🏷️ [TAGS DEBUG] layerMetadata.tags:',
              state.lastGenerated?.layerMetadata?.tags
            );
            console.log(
              '🏷️ [TAGS DEBUG] direct tags:',
              state.lastGenerated?.tags
            );
            console.log('🏷️ [TAGS DEBUG] final richTags:', richTags);

            return richTags;
          })()}
          processingType={state.lastGenerated?.processingType}
          layerMetadata={state.lastGenerated?.layerMetadata}
          layer={layer}
          isGenerating={state.isGenerating}
        />
      )}

      {/* Tag Quality Indicator */}
      {(() => {
        const hasGenerated =
          state.lastGenerated?.description || state.lastGenerated?.tags;
        // 🎯 CRITICAL FIX: Check comprehensive tags from starsMetadata.tags (consistent with fixes above)
        const tags =
          state.lastGenerated?.starsMetadata?.tags ||
          state.lastGenerated?.tags ||
          state.lastGenerated?.layerMetadata?.tags ||
          [];
        return (
          hasGenerated && !state.isGenerating && !state.error && tags.length > 0
        );
      })() && (
        <TagQualityIndicator
          tags={(() => {
            // 🎯 CRITICAL FIX: Read comprehensive tags from starsMetadata.tags (30 tags) instead of aiMetadata.tags (6 tags)
            // Priority order: starsMetadata.tags -> root tags -> layerMetadata.tags -> fallback
            const richTags =
              state.lastGenerated?.starsMetadata?.tags ||
              state.lastGenerated?.tags ||
              state.lastGenerated?.layerMetadata?.tags ||
              [];

            console.log('🏷️ [TAG DISPLAY FIX] Tag sources:', {
              starsMetadataTags:
                state.lastGenerated?.starsMetadata?.tags?.length || 0,
              rootTags: state.lastGenerated?.tags?.length || 0,
              layerMetadataTags:
                state.lastGenerated?.layerMetadata?.tags?.length || 0,
              finalCount: richTags.length,
            });

            return richTags;
          })()}
          metadata={(() => {
            const layerMetadata = state.lastGenerated?.layerMetadata;
            if (!layerMetadata) return undefined;

            return {
              hairColor: layerMetadata.hairColor,
              hairStyle: layerMetadata.hairStyle,
              makeupType: layerMetadata.makeupType,
              musicalStyle: layerMetadata.musicalStyle,
              ageGroup: layerMetadata.ageGroup,
              archetype: layerMetadata.archetype,
              gender: layerMetadata.gender,
              eyeColor: layerMetadata.eyeColor,
              skinTone: layerMetadata.skinTone,
            };
          })()}
          compact={false}
          showDetails={false}
          onAddTag={(newTag: string) => {
            // 🎯 CRITICAL FIX: Get current tags from starsMetadata.tags (consistent with display fix above)
            const raw =
              state.lastGenerated?.starsMetadata?.tags ||
              state.lastGenerated?.tags ||
              state.lastGenerated?.layerMetadata?.tags ||
              [];
            const currentTags = Array.isArray(raw)
              ? raw
              : raw && typeof raw === 'object'
                ? Object.values(raw).filter((t): t is string => typeof t === 'string')
                : [];

            // Add new tag if not already present
            if (!currentTags.includes(newTag)) {
              const updatedTags = [...currentTags, newTag];

              // 🎯 CRITICAL FIX: Update state immediately for UI responsiveness (update all tag locations)
              setState(prev => ({
                ...prev,
                lastGenerated: prev.lastGenerated
                  ? {
                      ...prev.lastGenerated,
                      tags: updatedTags,
                      starsMetadata: prev.lastGenerated.starsMetadata
                        ? {
                            ...prev.lastGenerated.starsMetadata,
                            tags: updatedTags,
                          }
                        : undefined,
                      layerMetadata: prev.lastGenerated.layerMetadata
                        ? {
                            ...prev.lastGenerated.layerMetadata,
                            tags: updatedTags,
                          }
                        : undefined,
                    }
                  : null,
              }));

              // Notify parent component to update form state
              onTagsGenerated(updatedTags);
            }
          }}
        />
      )}

      {/* Warning Message for Content Policy Issues */}
      {state.lastGenerated?.warning && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>⚠️ Content Restriction Notice:</strong>{' '}
            {state.lastGenerated.warning}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: 'block' }}
          >
            AI analysis was limited for this content. You can still use the
            asset with the current description and tags, or try uploading
            different content for enhanced AI processing.
          </Typography>
        </Alert>
      )}

      {/* Enhanced Error Message with Retry Functionality */}
      {state.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {typeof state.error === 'string' ? state.error : 'An error occurred during AI processing'}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={() => {
                setState(prev => ({ ...prev, error: null }));
                handleGenerateMetadata();
              }}
              disabled={state.isGenerating}
            >
              Try Again
            </Button>

            <Button
              size="small"
              variant="outlined"
              onClick={() => setState(prev => ({ ...prev, error: null }))}
            >
              Dismiss
            </Button>

            {!canGenerateMetadata() && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ alignSelf: 'center' }}
              >
                Upload a file to generate metadata.
              </Typography>
            )}
          </Box>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 1 }}
          >
            💡 If the problem persists, try uploading a different file or contact support.
          </Typography>
        </Alert>
      )}

      {/* Simple Album Art Display for Songs */}
      {state.lastGenerated &&
        layer === 'G' &&
        (state.lastGenerated.additionalMetadata?.albumArt ||
          state.lastGenerated.layerMetadata?.albumArt) && (
          <Box
            sx={{
              mt: 2,
              display: 'flex',
              alignItems: 'center',
              p: 2,
              bgcolor: 'background.paper',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <img
              src={
                state.lastGenerated.additionalMetadata?.albumArt ||
                state.lastGenerated.layerMetadata?.albumArt
              }
              alt="Album Art"
              style={{
                width: 60,
                height: 60,
                borderRadius: 8,
                marginRight: 16,
              }}
            />
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {state.lastGenerated.layerMetadata?.songName || 'Unknown Song'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                by{' '}
                {state.lastGenerated.layerMetadata?.artistName ||
                  'Unknown Artist'}
              </Typography>
              {state.lastGenerated.layerMetadata?.albumName && (
                <Typography variant="caption" color="text.secondary">
                  from {state.lastGenerated.layerMetadata.albumName}
                </Typography>
              )}
            </Box>
          </Box>
        )}

      {/* Enhanced Help Text */}
      <Box
        sx={{
          mt: 2,
          p: 2,
          bgcolor: 'background.paper',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          <strong>How AI Contract works:</strong> Using the AI Response Contract
          specification, the backend provides consistent, validated metadata
          generation:
          {layer === 'G' &&
            ' song data extraction + BPM detection + energy analysis + mood detection + album art lookup'}
          {layer === 'S' &&
            ' performer analysis + image context + performance attributes + archetype detection'}
          {layer === 'L' &&
            ' style analysis + fashion context + aesthetic matching + brand recognition'}
          {layer === 'M' &&
            ' movement analysis + choreography context + rhythm matching + complexity assessment'}
          {layer === 'W' &&
            ' environment analysis + atmosphere context + mood matching + time-of-day detection'}
          {layer === 'C' &&
            ' component aggregation + composite intelligence + cross-layer optimization + intelligent tag merging'}
          {!['G', 'S', 'L', 'M', 'W', 'C'].includes(layer) &&
            ' context-aware processing'}
          . All responses follow the contract specification with validation for
          data consistency and type safety.
        </Typography>

        {/* Composite-specific help */}
        {layer === 'C' && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              bgcolor: 'rgba(25, 118, 210, 0.08)',
              borderRadius: 1,
              border: '1px solid rgba(25, 118, 210, 0.2)',
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: 'primary.main', fontWeight: 'bold' }}
            >
              🎭 Composite Layer Special Features:
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              component="div"
              sx={{ mt: 0.5 }}
            >
              • <strong>Smart Aggregation:</strong> Combines metadata from G, S,
              L, M, W components
              <br />• <strong>Tag Intelligence:</strong> Frequency analysis
              finds most relevant tags
              <br />• <strong>Cross-Layer Optimization:</strong> Ensures
              compatibility across all component types
              <br />• <strong>Rich Descriptions:</strong> Creates unified
              narrative from component descriptions
            </Typography>
          </Box>
        )}
      </Box>

      <Divider sx={{ mt: 2 }} />
    </Box>
  );
};

export default AIMetadataGenerator;
